import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { MaxBrainUtils } from 'app/projects/core/src/lib/utils';
import { EntityState } from 'app/projects/entity/src/lib/models/entity.state';
import {
    CreateScheduledStreamContent,
    CreateScheduledStreamContentFailure,
    CreateScheduledStreamContentSuccess,
    CreateValidScheduledStreamContent,
    CreateValidScheduledStreamContentBulk,
    CreateValidScheduledStreamContentBulkFailure,
    CreateValidScheduledStreamContentBulkSuccess,
    CreateValidScheduledStreamContentFailure,
    CreateValidScheduledStreamContentSuccess,
    DeleteScheduledStreamContent,
    DeleteScheduledStreamContentFailure,
    DeleteScheduledStreamContentSuccess,
    FetchScheduledStreamContent,
    FetchScheduledStreamContentFailure,
    FetchScheduledStreamContentSuccess,
    SetScheduledStreamContent,
    SetScheduledStreamContents,
    UnsetScheduledStreamContent,
    UpdateScheduledStreamContent,
    UpdateScheduledStreamContentFailure,
    UpdateScheduledStreamContentSuccess,
} from '../actions';
import { AddScheduledStreamContents } from '../actions/add-entities.action';
import { ScheduledStreamContentApiService } from '../services/scheduled-stream-content.api-service';
import { MaxBrainScheduledStreamContent } from './scheduled-stream-content.model';
import { ScheduledStreamContentStateModel } from './scheduled-stream-content.state-model';

@State<ScheduledStreamContentStateModel>({
    name: 'scheduledStreamContent',
    defaults: new ScheduledStreamContentStateModel(),
})
@Injectable()
export class ScheduledStreamContentState extends EntityState<MaxBrainScheduledStreamContent> {
    static sortProperties: string[] = ['name'];

    @Selector()
    static getEntity(state: ScheduledStreamContentStateModel, id: string = state.item): MaxBrainScheduledStreamContent {
        if (!state.map) {
            return null;
        }

        return state.map.get(id) || null;
    }

    @Selector()
    static getEntities(state: ScheduledStreamContentStateModel): MaxBrainScheduledStreamContent[] {
        if (!state.map) {
            return null;
        }

        const toReturn = Array.from(state.map.values());

        toReturn.sort(MaxBrainUtils.sortCompareFn(ScheduledStreamContentState.sortProperties));

        return toReturn;
    }

    @Selector()
    static getEntitiesAsMap(state: ScheduledStreamContentStateModel): Map<string, MaxBrainScheduledStreamContent> {
        if (!state.map) {
            return null;
        }
        return state.map;
    }

    constructor(private _scheduledStreamContentService: ScheduledStreamContentApiService) {
        super(_scheduledStreamContentService);

        this._propstoSearch = MaxBrainScheduledStreamContent.props_to_search;
    }

    @Action(AddScheduledStreamContents)
    addEntities(ctx: StateContext<ScheduledStreamContentStateModel>, action: AddScheduledStreamContents): void {
        this._addEntities(ctx, action);
    }

    @Action(SetScheduledStreamContent)
    setEntity(ctx: StateContext<ScheduledStreamContentStateModel>, action: SetScheduledStreamContent): void {
        this._setEntity(ctx, action, AddScheduledStreamContents);
    }

    @Action(UnsetScheduledStreamContent)
    unsetEntity(ctx: StateContext<ScheduledStreamContentStateModel>): void {
        this._unsetEntity(ctx);
    }

    @Action(CreateScheduledStreamContent)
    createEntity(ctx: StateContext<ScheduledStreamContentStateModel>, action: CreateScheduledStreamContent): void {
        this._createEntity(ctx, action, CreateScheduledStreamContentSuccess, CreateScheduledStreamContentFailure, AddScheduledStreamContents).then(() => {});
    }

    @Action(CreateValidScheduledStreamContent)
    createScheduledStreamContent(ctx: StateContext<ScheduledStreamContentStateModel>, { payload }: CreateValidScheduledStreamContent): void {
        this._scheduledStreamContentService.createScheduledStreamContent(payload).subscribe(
            (scss) => {
                ctx.dispatch(new CreateValidScheduledStreamContentSuccess(scss));
            },
            (e) => {
                ctx.dispatch(new CreateValidScheduledStreamContentFailure(e));
            }
        );
    }

    @Action(CreateValidScheduledStreamContentBulk)
    createValidScheduledStreamContentBulk(ctx: StateContext<ScheduledStreamContentStateModel>, { payload }: CreateValidScheduledStreamContentBulk): void {
        this._scheduledStreamContentService.createScheduledStreamContentBulk(payload).subscribe(
            (scss) => {
                ctx.dispatch(new CreateValidScheduledStreamContentBulkSuccess(scss));
            },
            (e) => {
                ctx.dispatch(new CreateValidScheduledStreamContentBulkFailure(e));
            }
        );
    }

    @Action(UpdateScheduledStreamContent)
    updateEntity(ctx: StateContext<ScheduledStreamContentStateModel>, action: UpdateScheduledStreamContent): void {
        this._updateEntity(
            ctx,
            action,
            UpdateScheduledStreamContentSuccess,
            UpdateScheduledStreamContentFailure,
            AddScheduledStreamContents,
            this._scheduledStreamContentService.updateScheduledStreamContent(action.payload)
        );
    }

    @Action(DeleteScheduledStreamContent)
    deleteEntity(ctx: StateContext<ScheduledStreamContentStateModel>, action: DeleteScheduledStreamContent): void {
        this._deleteEntity(ctx, action, DeleteScheduledStreamContentSuccess, DeleteScheduledStreamContentFailure).then(() => {});
    }

    @Action(FetchScheduledStreamContent)
    fetchEntity(ctx: StateContext<ScheduledStreamContentStateModel>, action: FetchScheduledStreamContent): void {
        this._fetchEntity(ctx, action, FetchScheduledStreamContentSuccess, FetchScheduledStreamContentFailure, AddScheduledStreamContents, SetScheduledStreamContent).then(
            () => {}
        );
    }

    @Action(FetchScheduledStreamContentSuccess)
    fetchEntitySuccess(ctx: StateContext<ScheduledStreamContentStateModel>, action: FetchScheduledStreamContentSuccess): void {
        this._fetchEntitySuccess(ctx, action);
    }

    @Action(FetchScheduledStreamContentFailure)
    fetchEntityFailure(ctx: StateContext<ScheduledStreamContentStateModel>, action: FetchScheduledStreamContentFailure): void {
        this._fetchEntityFailure(ctx, action);
    }

    @Action(SetScheduledStreamContents)
    setEntities(ctx: StateContext<ScheduledStreamContentStateModel>, action: SetScheduledStreamContents): void {
        this._setEntities(ctx, action);
    }
}
